import { useEffect, useRef, useState } from "react";

interface CaptchaWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (error: unknown) => void;
}

declare global {
  interface Window {
    hcaptcha?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: (error: unknown) => void;
          hl?: string;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const HCAPTCHA_SCRIPT_URL =
  "https://js.hcaptcha.com/1/api.js?render=explicit&hl=de";

/**
 * Dynamically loads the hCaptcha script if not already present.
 * Returns a promise that resolves when window.hcaptcha is available.
 */
function loadHCaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.hcaptcha) {
      resolve();
      return;
    }

    // Script tag already exists but hasn't finished loading
    const existing = document.querySelector(
      `script[src^="https://js.hcaptcha.com"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("hCaptcha script failed to load")),
      );
      return;
    }

    // Create and append script
    const script = document.createElement("script");
    script.src = HCAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("hCaptcha script failed to load")),
    );
    document.head.appendChild(script);
  });
}

export default function CaptchaWidget({
  siteKey,
  onVerify,
  onExpire,
  onError,
}: CaptchaWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(!!window.hcaptcha);

  // Store callbacks in refs so the render effect doesn't re-trigger
  // when parent re-renders with new inline function references
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  // Load the hCaptcha script dynamically
  useEffect(() => {
    if (scriptLoaded) return;

    let cancelled = false;
    loadHCaptchaScript()
      .then(() => {
        if (!cancelled) setScriptLoaded(true);
      })
      .catch((err) => {
        console.error("CaptchaWidget: Failed to load hCaptcha script:", err);
        onErrorRef.current?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [scriptLoaded]);

  // Render the widget once the script is loaded (only depends on scriptLoaded + siteKey)
  useEffect(() => {
    if (!scriptLoaded || !containerRef.current || !window.hcaptcha) return;

    // Render hCaptcha widget with stable ref-based callbacks
    widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token: string) => onVerifyRef.current(token),
      "expired-callback": () => onExpireRef.current?.(),
      "error-callback": (error: unknown) => onErrorRef.current?.(error),
      hl: "de",
    });

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch {
          // Widget may already be removed
        }
      }
    };
  }, [scriptLoaded, siteKey]);

  return <div ref={containerRef} className="h-captcha" />;
}
